// Core hook (opt-in): stacked division standings (#583, DECISIONS 4, API.md
// §4.3). Under "All divisions" it is one table per live division, in admin
// order, from GET /public/standings/divisions; with one division picked it is
// that division's table, from GET /public/standings?division=<id>. Teams of
// different divisions are NEVER ranked in one merged table.
//
// Lazy like useCompetitionStandings: nothing is fetched unless the caller
// passes `enabled` AND the sport has 2+ live divisions, so a league without
// divisions (and Classic, which calls it only at 2+) never sees the request.
// Templates render each table with its own <caption> and an <h2> (UX §6).

import { useCallback, useMemo, useState } from 'react'
import { api } from '@/services/api'
import { useLeague } from '@/contexts/LeagueContext'
import { useSport, useSportContext, type StandingsColumnDef } from '@/contexts/SportContext'
import type { DivisionRef, DivisionStandingsResponse, RankingStrategyInfo, TeamStanding } from '@/services/api/types'
import { standingRows } from '../pages/home/derive'
import type { StandingRowVM } from '../pages/home/types'
import { divisionRef, useDivision } from '../division'
import { useSharedRequest } from './shared'

export const DIVISION_STANDINGS_TTL_MS = 60_000

export interface DivisionStandingsTable {
  division: DivisionRef
  /** The division's public season the table is for; null when unknown. */
  season: { id: number; name: string | null; year: number } | null
  /** The strategy's columns (win % adds PCT, #651), else the sport's. */
  columns: StandingsColumnDef[]
  rows: StandingRowVM[]
  /** The season's ranking strategy, when the API sent it. */
  rankingStrategy: RankingStrategyInfo | null
}

export interface DivisionStandingsState {
  /** 'idle': not enabled, or fewer than 2 live divisions (nothing fetched). */
  status: 'idle' | 'loading' | 'ready' | 'error'
  /** All divisions: one per live division with a table; one division: just its own. Never merged. */
  tables: DivisionStandingsTable[]
  retry: () => void
}

type StackedEntry = Omit<DivisionStandingsResponse['divisions'][number], 'season'> & { season: DivisionStandingsTable['season'] }

const IDLE: DivisionStandingsState = { status: 'idle', tables: [], retry: () => {} }

/**
 * `opts.all`: always "All divisions", whatever the fan picked elsewhere (#710:
 * Classic Home has no switcher and shows every division, `homeDivision: 'all'`).
 */
export function useDivisionStandings(enabled: boolean, opts: { all?: boolean } = {}): DivisionStandingsState {
  const { league } = useLeague()
  const { activeSportId } = useSportContext()
  const sport = useSport()
  const division = useDivision()
  const value = !division.enabled ? null : opts.all ? 'all' : division.value
  const run = enabled && value != null
  const [attempt, setAttempt] = useState(0)
  const key = run ? `division-standings:${activeSportId}:${value}:${attempt}` : null
  const res = useSharedRequest<StackedEntry[]>(key, DIVISION_STANDINGS_TTL_MS, async () => {
    if (value === 'all') return (await api.getDivisionStandings()).divisions ?? []
    const one = division.active!
    const r = (await api.getStandings(undefined, { division: one.id })) as { standings?: TeamStanding[]; rankingStrategy?: RankingStrategyInfo }
    const season = (league?.seasons ?? []).find((s) => s.id === one.public_season_id)
    return [{
      division: divisionRef(division.live, one.id)!,
      season: season ? { id: season.id, name: season.name, year: season.year } : null,
      standings: r.standings ?? [],
      rankingStrategy: r.rankingStrategy,
    }]
  })
  const tables = useMemo<DivisionStandingsTable[]>(
    () => (res.value ?? []).map((d) => ({
      division: d.division,
      season: d.season ?? null,
      columns: d.rankingStrategy?.columns ?? sport.standings.columns,
      rows: standingRows(d.standings ?? []),
      rankingStrategy: d.rankingStrategy ?? null,
    })),
    [res.value, sport],
  )
  const retry = useCallback(() => setAttempt((n) => n + 1), [])
  if (!run) return IDLE
  return { status: res.status, tables, retry }
}
